import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../../providers/NotificationProvider";
import { BSC_CATEGORY_CONFIG } from "./bscConfig";

const formatDate = (iso) => {
  if (!iso) return "—";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function BscAchievementListPage() {
  const { businessDomainId, businessUnitId, category, goalIdx } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);

  const config = BSC_CATEGORY_CONFIG[category];
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/bsc/${category}`;

  const loadData = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      const data = await config.fetchGoals(businessUnitId);
      const arr = Array.isArray(data) ? data : [];
      const row = arr[Number(goalIdx)];
      setAchievements(row?.achievements || []);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, category, goalIdx]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`${basePath}/${goalIdx}`)}
          variant="text"
        >
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t("bsc.achievement.list.title")}
        </Typography>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : achievements.length === 0 ? (
        <Typography color="text.secondary">{t("common.noData")}</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>{t("bsc.achievement.actualValue")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("bsc.achievement.actualDate")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {achievements.map((a, idx) => (
                <TableRow key={idx}>
                  <TableCell>{a.actualValue ?? "—"}</TableCell>
                  <TableCell>{formatDate(a.actualDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
